import { currentAnnouncementSchema } from '@oefy/contracts';
import { useQuery } from '@tanstack/react-query';
import { Info, TriangleAlert, X } from 'lucide-react';
import { useState } from 'react';
import { api } from '../lib/api';
import { cx } from '../ui/styles';

const DISMISSED = 'oefy-announcement-dismissed';

function readDismissed(): string | null {
  try {
    return window.localStorage.getItem(DISMISSED);
  } catch {
    return null;
  }
}

function writeDismissed(id: string): void {
  try {
    window.localStorage.setItem(DISMISSED, id);
  } catch {
    return;
  }
}

export function AnnouncementBar({ audience }: { readonly audience: 'parents' | 'children' }) {
  const [dismissed, setDismissed] = useState(readDismissed);
  const current = useQuery({
    queryKey: ['announcement', audience],
    queryFn: () => api.get(`/announcements/current?for=${audience}`, currentAnnouncementSchema),
    staleTime: 5 * 60_000,
  });
  const announcement = current.data?.announcement;
  if (!announcement || announcement.id === dismissed) {
    return null;
  }
  const warning = announcement.tone === 'warning';
  const Icon = warning ? TriangleAlert : Info;
  return (
    <div
      role="status"
      className={cx(
        'relative z-40 px-4 py-2.5',
        warning ? 'bg-amber-300 text-amber-950' : 'bg-brand-700 text-white',
        audience === 'children' ? 'font-child text-base font-semibold' : 'text-sm',
      )}
    >
      <div className="mx-auto flex max-w-5xl items-start gap-3">
        <Icon aria-hidden="true" className="mt-0.5 size-5 shrink-0" />
        <p className="flex-1 leading-relaxed">{announcement.message}</p>
        <button
          type="button"
          aria-label="Melding sluiten"
          onClick={() => {
            writeDismissed(announcement.id);
            setDismissed(announcement.id);
          }}
          className={cx(
            '-m-1 flex size-8 shrink-0 items-center justify-center rounded-full outline-none transition focus-visible:ring-2',
            warning
              ? 'hover:bg-amber-400 focus-visible:ring-amber-900'
              : 'hover:bg-brand-800 focus-visible:ring-white',
          )}
        >
          <X aria-hidden="true" className="size-4" />
        </button>
      </div>
    </div>
  );
}
